import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlane, 
  FaHotel, 
  FaTrain, 
  FaCar, 
  FaPassport,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus
} from 'react-icons/fa';

const BookingList = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const savedBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
    const formattedBookings = savedBookings.map((booking, index) => ({
      id: booking.id || `BK${String(index + 1).padStart(4, '0')}`,
      customer: booking.customer,
      services: booking.bookings,
      totalItems: booking.totalItems || 0,
      date: new Date(booking.timestamp).toLocaleDateString(),
      time: new Date(booking.timestamp).toLocaleTimeString(),
      status: booking.status || 'Active',
      lastUpdated: booking.lastUpdated,
      ...booking
    }));
    setBookings(formattedBookings);
    setFilteredBookings(formattedBookings);
  }, []);

  useEffect(() => {
    let filtered = bookings.filter(booking => {
      const matchesSearch = booking.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           booking.customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           booking.id.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDate = !dateFilter || booking.date.includes(dateFilter);
      const matchesStatus = statusFilter === 'all' || booking.status.toLowerCase() === statusFilter;
      
      return matchesSearch && matchesDate && matchesStatus;
    });
    
    setFilteredBookings(filtered);
  }, [bookings, searchTerm, dateFilter, statusFilter]);

  const getServiceIcons = (services) => {
    const serviceTypes = Object.entries(services).filter(([_, items]) => items.length > 0);
    const iconMap = {
      flights: { icon: FaPlane, color: 'bg-blue-500' },
      hotels: { icon: FaHotel, color: 'bg-green-500' },
      trains: { icon: FaTrain, color: 'bg-purple-500' },
      cars: { icon: FaCar, color: 'bg-orange-500' },
      visas: { icon: FaPassport, color: 'bg-red-500' }
    };

    return serviceTypes.map(([type, items]) => {
      const IconComponent = iconMap[type]?.icon || FaPlane;
      const colorClass = iconMap[type]?.color || 'bg-gray-500';
      
      return (
        <div key={type} className={`${colorClass} p-2 rounded-full text-white relative`}>
          <IconComponent className="h-4 w-4" />
          {items.length > 1 && (
            <span className="absolute -top-1 -right-1 bg-white text-xs rounded-full h-5 w-5 flex items-center justify-center text-gray-700 font-bold">
              {items.length}
            </span>
          )}
        </div>
      );
    });
  };

  const handleDeleteBooking = (bookingId) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      const updatedBookings = bookings.filter(booking => booking.id !== bookingId);
      setBookings(updatedBookings);
      setFilteredBookings(updatedBookings);
      
      // Update localStorage
      const savedBookings = JSON.parse(localStorage.getItem('flightsBookings') || '[]');
      const filteredSavedBookings = savedBookings.filter(booking => booking.id !== bookingId);
      localStorage.setItem('flightsBookings', JSON.stringify(filteredSavedBookings));
    }
  };

  const StatusBadge = ({ status }) => {
    const statusColors = {
      Active: 'bg-green-100 text-green-800 border-green-200',
      Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Completed: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200',
      Cancelled: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[status] || statusColors.Active}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-secondary-900">Flight Bookings</h1>
          <p className="text-secondary-600">Manage and track all flight reservations</p>
        </div>
        <button
          onClick={() => navigate('/booking')}
          className="mt-4 sm:mt-0 flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-3 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg"
        >
          <FaPlus className="h-4 w-4" />
          <span>New Booking</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-card border border-secondary-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex items-center space-x-2 text-secondary-700">
            <FaFilter className="h-4 w-4" />
            <span className="font-medium">Filters:</span>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1 lg:max-w-2xl">
            {/* Search */}
            <div className="relative flex-1">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by customer name, email, or booking ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            {/* Date Filter */}
            <div className="relative">
              <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-4 w-4" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="pl-10 pr-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bookings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredBookings.length === 0 ? (
          <div className="col-span-full">
            <div className="bg-white rounded-xl shadow-card border border-secondary-100 p-12 text-center">
              <FaPlane className="h-16 w-16 text-secondary-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">No bookings found</h3>
              <p className="text-secondary-600 mb-6">
                {searchTerm || dateFilter || statusFilter !== 'all' 
                  ? 'Try adjusting your filters to see more results.'
                  : 'Get started by creating your first flight booking.'
                }
              </p>
              <button
                onClick={() => navigate('/booking')}
                className="btn-primary"
              >
                Create New Booking
              </button>
            </div>
          </div>
        ) : (
          filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-xl shadow-card border border-secondary-100 hover:shadow-business transition-shadow">
              {/* Header */}
              <div className="p-6 border-b border-secondary-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {booking.customer.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-secondary-900">{booking.customer.name}</h3>
                      <p className="text-sm text-secondary-600">{booking.customer.id}</p>
                    </div>
                  </div>
                  <StatusBadge status={booking.status} />
                </div>

                <div className="flex items-center space-x-4 text-sm text-secondary-600">
                  <div className="flex items-center space-x-1">
                    <FaCalendarAlt className="h-3 w-3" />
                    <span>{booking.date}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FaUser className="h-3 w-3" />
                    <span>{booking.totalItems} services</span>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-secondary-900">Services</h4>
                  <div className="flex items-center space-x-2">
                    {getServiceIcons(booking.services)}
                  </div>
                </div>

                <div className="space-y-2">
                  {Object.entries(booking.services).filter(([_, items]) => items.length > 0).map(([type, items]) => (
                    <div key={type} className="flex justify-between items-center text-sm">
                      <span className="text-secondary-600 capitalize">{type}:</span>
                      <span className="font-medium text-secondary-900">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-secondary-50 border-t border-secondary-100 rounded-b-xl">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-secondary-500">
                    Created: {booking.time}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => navigate(`/booking?bookingId=${booking.id}`)}
                      className="p-2 text-secondary-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <FaEye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/booking?bookingId=${booking.id}`)}
                      className="p-2 text-secondary-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit Booking"
                    >
                      <FaEdit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBooking(booking.id)}
                      className="p-2 text-secondary-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Booking"
                    >
                      <FaTrash className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary Stats */}
      {filteredBookings.length > 0 && (
        <div className="bg-white rounded-xl shadow-card border border-secondary-100 p-6">
          <h3 className="font-semibold text-secondary-900 mb-4">Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{filteredBookings.length}</div>
              <div className="text-sm text-secondary-600">Total Bookings</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {filteredBookings.filter(b => b.status === 'Active' || b.status === 'active').length}
              </div>
              <div className="text-sm text-secondary-600">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {filteredBookings.filter(b => b.status === 'Pending' || b.status === 'pending').length}
              </div>
              <div className="text-sm text-secondary-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {filteredBookings.filter(b => b.status === 'Completed' || b.status === 'completed').length}
              </div>
              <div className="text-sm text-secondary-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {filteredBookings.reduce((sum, b) => sum + b.totalItems, 0)}
              </div>
              <div className="text-sm text-secondary-600">Total Services</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingList;